import type { Todo } from '@ext-bookmark/shared/types';

/**
 * 判断任务类型并返回对应的颜色类名
 */
export const getTodoDotColor = (todo: Todo, currentUserId: string | null): string => {
  // 优先级：分配给我的任务 > 家庭任务 > 个人任务
  if (currentUserId && todo.assignedTo && todo.assignedTo.includes(currentUserId)) {
    return 'bg-orange-500 dark:bg-orange-400'; // 橙色：分配给我的任务
  }
  if (todo.groupId) {
    return 'bg-green-500 dark:bg-green-400'; // 绿色：分组任务
  }
  return 'bg-blue-500 dark:bg-blue-400'; // 蓝色：个人分组任务
};

/**
 * 获取日期的主要任务类型（用于显示小圆点）
 */
export const getDayTodoType = (todos: Todo[], currentUserId: string | null): 'assigned' | 'group' | 'personal' | null => {
  if (todos.length === 0) return null;
  // 优先级：分配给我的任务 > 分组任务 > 个人任务
  if (currentUserId) {
    const hasAssigned = todos.some(todo => todo.assignedTo && todo.assignedTo.includes(currentUserId));
    if (hasAssigned) return 'assigned';
  }
  const hasGroup = todos.some(todo => todo.groupId);
  if (hasGroup) return 'group';
  return 'personal';
};

/**
 * 日历按日分组的 key：YYYY-MM-DD，与 locale 无关，避免 toDateString() 导致同一天对不上
 */
export const toCalendarDateKey = (date: Date | number): string => {
  const d = typeof date === 'number' ? new Date(date) : date;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * 判断是否是今天
 */
export const isToday = (date: Date | null): boolean => {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * 判断是否是当前月份
 */
export const isCurrentMonth = (date: Date | null, month: number): boolean => {
  if (!date) return false;
  return date.getMonth() === month;
};

/**
 * 判断是否被选中
 */
export const isSelected = (date: Date | null, selectedDate: Date | null): boolean => {
  if (!date || !selectedDate) return false;
  return (
    date.getDate() === selectedDate.getDate() &&
    date.getMonth() === selectedDate.getMonth() &&
    date.getFullYear() === selectedDate.getFullYear()
  );
};

/**
 * 生成日历日期数组
 */
export const generateCalendarDays = (
  year: number,
  month: number,
  firstDayOfWeek: number,
  isMobile: boolean
): (Date | null)[] => {
  const calendarDays: (Date | null)[] = [];
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  
  if (isMobile) {
    // 移动端：只显示当前月份的日期
    // 填充上个月的日期（用于对齐，但不显示）
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push(null);
    }
    // 填充当月的日期
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, month, day));
    }
  } else {
    // 桌面端：保持原有逻辑
    // 填充上个月的日期
    for (let i = 0; i < firstDayOfWeek; i++) {
      const date = new Date(year, month, -i);
      calendarDays.unshift(date);
    }
    // 填充当月的日期
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, month, day));
    }
    // 填充下个月的日期（补齐到42个格子，6行x7列）
    const remainingDays = 42 - calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      calendarDays.push(new Date(year, month + 1, day));
    }
  }
  
  return calendarDays;
};

/**
 * 计算日历日期信息
 */
export const getCalendarDateInfo = (currentDate: Date, isMobile: boolean) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  
  // 计算当前月份的实际行数
  const totalDaysForCurrentMonth = firstDayOfWeek + daysInMonth;
  const actualRows = Math.ceil(totalDaysForCurrentMonth / 7);
  
  const calendarDays = generateCalendarDays(year, month, firstDayOfWeek, isMobile);
  
  return {
    year,
    month,
    firstDay,
    lastDay,
    firstDayOfWeek,
    daysInMonth,
    calendarDays,
    actualRows,
  };
};
